import React, { ReactNode, FunctionComponent, useState, useRef } from 'react'
import clsx from 'clsx'
import styles from './rating.module.scss'

import useControlled from '../utils/useControlled'
import useIsFocusVisible from '../utils/useIsFocusVisible'
import useId from '../utils/useId'
import DisplayRatings from './DisplayRatings'
import { IRatingProps } from './interfaces/ratings'

// code idea from https://github.com/mui-org/material-ui/blob/next/packages/material-ui-lab/src/Rating/Rating.js

function clamp(value: number, min: number, max: number) {
  if (value < min) {
    return min
  }
  if (value > max) {
    return max
  }
  return value
}

function getDecimalPrecision(num: number) {
  const decimalPart = num.toString().split('.')[1]
  return decimalPart ? decimalPart.length : 0
}

function roundValueToPrecision(value: number, precision: number) {
  if (value == null) {
    return value
  }

  const nearest = Math.round(value / precision) * precision
  return Number(nearest.toFixed(getDecimalPrecision(precision)))
}

function defaultLabelText(value: number) {
  return `${value} Star${value !== 1 ? 's' : ''}`
}

const Rating: FunctionComponent<IRatingProps> = ({
  ratingType = '',
  className = '',
  defaultValue = null,
  disabled = false,
  emptyLabelText = 'Empty',
  isMouseMove = true,
  getLabelText = defaultLabelText,
  max = 5,
  startingIndex = 0,
  name: nameProp,
  onChange,
  onChangeActive,
  onMouseLeave,
  onMouseMove,
  precision = 1,
  readOnly = false,
  size = 'md',
  value: valueProp,
  ...other
}) => {
  const name = useId(nameProp)

  const isNumericRating = ratingType === 'numeric'
  const [valueDerived, setValueState] = useControlled({
    controlled: valueProp,
    defaultValue
  })

  const valueRounded = roundValueToPrecision(valueDerived, precision)
  const [{ hover, focus }, setState] = useState({
    hover: -1,
    focus: -1
  })

  let value = valueRounded
  if (hover !== -1) {
    value = hover
  }
  if (focus !== -1) {
    value = focus
  }

  const { isFocusVisibleRef, onBlur: handleBlurVisible, onFocus: handleFocusVisible } = useIsFocusVisible()
  const [focusVisible, setFocusVisible] = useState(false)

  const rootRef = useRef()

  const handleMouseMove = (event: any) => {
    if (isMouseMove === false) {
      return
    }
    if (onMouseMove) {
      onMouseMove(event)
    }

    const rootNode = rootRef.current

    if (!rootNode) {
      return
    }
    // @ts-ignore
    const { left } = rootNode.getBoundingClientRect()
    // @ts-ignore
    const { width } = rootNode.firstChild.getBoundingClientRect()
    const numericScaleWidthFactor = isNumericRating ? (max - 1) * 10 : 0
    const percent = (event.clientX - left) / (width * max + numericScaleWidthFactor)

    let newHover = roundValueToPrecision(max * percent + precision / 2, precision)
    newHover = clamp(newHover, precision, max)

    setState((prev) =>
      prev.hover === newHover && prev.focus === newHover
        ? prev
        : {
            hover: newHover,
            focus: newHover
          }
    )

    setFocusVisible(false)

    if (onChangeActive && hover !== newHover) {
      onChangeActive(event, newHover)
    }
  }

  const handleMouseLeave = (event: any) => {
    if (onMouseLeave) {
      onMouseLeave(event)
    }

    const newHover = -1
    setState({
      hover: newHover,
      focus: newHover
    })

    if (onChangeActive && hover !== newHover) {
      onChangeActive(event, newHover)
    }
  }

  const handleChange = (event: any) => {
    const newValue = parseFloat(event.target.value)

    setValueState(newValue)

    if (onChange) {
      const displayValue = isNumericRating ? newValue - 1 + startingIndex : newValue
      onChange(event, displayValue)
    }
  }

  const handleSameStarClick = (event: any) => {
    // Ignore keyboard events
    // https://github.com/facebook/react/issues/7407
    if (event.clientX === 0 && event.clientY === 0) {
      return
    }

    handleClear(event)
  }

  const handleSameStarKey = (event: any) => {
    const key = event.keyCode || event.which
    if (key === 13) {
      handleClear(event)
    }
  }

  const handleClear = (event: any) => {
    setState({
      hover: -1,
      focus: -1
    })

    setValueState(null)

    if (onChange && parseFloat(event.target.value) === valueRounded) {
      onChange(event, null)
    }
  }

  const handleFocus = (event: any) => {
    handleFocusVisible(event)
    if (isFocusVisibleRef.current === true) {
      setFocusVisible(true)
    }

    const newFocus = parseFloat(event.target.value)
    setState((prev) => ({
      hover: prev.hover,
      focus: newFocus
    }))

    if (onChangeActive && focus !== newFocus) {
      onChangeActive(event, newFocus)
    }
  }

  const handleBlur = (event: any) => {
    if (hover !== -1) {
      return
    }

    handleBlurVisible()
    if (isFocusVisibleRef.current === false) {
      setFocusVisible(false)
    }

    const newFocus = -1
    setState((prev) => ({
      hover: prev.hover,
      focus: newFocus
    }))

    if (onChangeActive && focus !== newFocus) {
      onChangeActive(event, newFocus)
    }
  }

  const item = (state: any, itemValue: number, labelProps = {}) => {
    const id = `${name}-${String(state.value).replace('.', '-')}`
    const container = <DisplayRatings state={state} numericRatingValue={itemValue} />

    if (readOnly) {
      return (
        <span key={state.value} {...labelProps}>
          {container}
        </span>
      )
    }

    return (
      <React.Fragment key={state.value}>
        <label className={styles.label} htmlFor={id} {...labelProps}>
          {container}
          <span className={styles.visuallyHidden}>{getLabelText(state.value)}</span>
        </label>
        <input
          onFocus={handleFocus}
          onBlur={handleBlur}
          onChange={handleChange}
          onClick={handleSameStarClick}
          onKeyPress={handleSameStarKey}
          disabled={disabled}
          value={state.value}
          id={id}
          type="radio"
          name={name}
          checked={state.checked}
          className={styles.visuallyHidden}
        />
      </React.Fragment>
    )
  }

  return (
    <span
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      // @ts-ignore
      ref={rootRef}
      className={clsx(
        styles.root,
        {
          [styles[`size-${size}`]]: size !== 'md',
          [styles.disabled]: disabled,
          [styles.focusVisible]: focusVisible,
          [styles.readOnly]: readOnly
        },
        className,
        isNumericRating && styles['numericScaleWidth']
      )}
      role={readOnly ? 'img' : undefined}
      aria-label={readOnly ? getLabelText(value) : undefined}
      {...other}
    >
      {Array.from(new Array(max)).map((_, index) => {
        const itemValue = index + 1

        let displayVaue = itemValue
        if (precision < 1 && !isNumericRating) {
          const items = Array.from(new Array(1 / precision))
          return (
            <span
              key={itemValue}
              className={clsx(styles.decimal, {
                [styles.iconActive]: itemValue === Math.ceil(value) && (hover !== -1 || focus !== -1)
              })}
            >
              {items.map(($, indexDecimal) => {
                const itemDecimalValue = roundValueToPrecision(
                  itemValue - 1 + (indexDecimal + 1) * precision,
                  precision
                )

                return item(
                  {
                    value: itemDecimalValue,
                    filled: itemDecimalValue <= value,
                    hover: itemDecimalValue <= hover,
                    focus: itemDecimalValue <= focus,
                    checked: itemDecimalValue === valueRounded,
                    isNumericRating: isNumericRating
                  },
                  itemValue,
                  {
                    style:
                      items.length - 1 === indexDecimal
                        ? {}
                        : {
                            width: itemDecimalValue === value ? `${(indexDecimal + 1) * precision * 100}%` : '0%',
                            overflow: 'hidden',
                            zIndex: 1,
                            position: 'absolute'
                          }
                  }
                )
              })}
            </span>
          )
        } else {
          // only for numeric ratings
          displayVaue = index === 0 ? startingIndex : startingIndex + index
        }

        return item(
          {
            value: itemValue,
            active: itemValue === value && (hover !== -1 || focus !== -1),
            filled: itemValue <= value,
            hover: itemValue <= hover,
            focus: itemValue <= focus,
            checked: itemValue === valueRounded,
            isNumericRating: isNumericRating,
            size: size
          },
          isNumericRating ? displayVaue : itemValue
        )
      })}
      {!readOnly && !disabled && valueRounded == null && (
        <>
          <input
            value=""
            id={`${name}-empty`}
            type="radio"
            name={name}
            defaultChecked
            className={styles.visuallyHidden}
          />
          <label className={styles.pristine} htmlFor={`${name}-empty`}>
            <span className={styles.visuallyHidden}>{emptyLabelText}</span>
          </label>
        </>
      )}
    </span>
  )
}

export default Rating
